import { useMemo, useState } from 'react';
import { TProductConfigResponse } from '../../types/product.configuration.types';
import { NudosStaticAlertInComponent } from '../NudosComponents';
import { ConfigurationStatusSection, ExternalWipingSection } from './Components';
import { AddSettingsModal } from './Components/AddSettingsModal/AddSettingsModal';
import { PRODUCT_CATEGORY } from '../../types/category';
import { TnudosCare } from '../../types/global';
import { CONFIG_CLASS_ID, IOrganizationConfig } from '../../types/product.configuration.types';
import BordOneToneIcon from '../NudosComponents/BordOneToneIcon/BordOneToneIcon';

import './ToolConfigurationsSection.scss';

export interface IConfigurationsData {
  productId: number;
  configurations: TProductConfigResponse[];
  orderId?: string;
  logisticServiceId?: number;
  organizationId?: number;
  productName?: string;
  category?: PRODUCT_CATEGORY | null;
  nudosCareName?: TnudosCare | null;
  savedOrganizationConfigs?: IOrganizationConfig[];
  hasConfigurations?: boolean;
  countryId?: number | null;
  countryCode?: string | null;
}

interface IToolConfigurationsSectionProps {
  configurationsData: IConfigurationsData;
  showExternalWipingSection: boolean;
  updateDataCallback: () => Promise<void>;
}

const ToolConfigurationsSection = ({
  showExternalWipingSection,
  updateDataCallback,
  configurationsData
}: IToolConfigurationsSectionProps) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const {
    orderId,
    logisticServiceId,
    productId,
    organizationId,
    configurations,
    productName,
    category,
    savedOrganizationConfigs,
    hasConfigurations,
    countryId,
    countryCode
  } = configurationsData;

  const isDEPLaptopsCategory = category === PRODUCT_CATEGORY.DEP_LAPTOPS;

  const title = 'Servicios solicitados';

  const alertOptions = {
    title: 'La plataforma notificará al usuario el estado de los servicios solicitados'
  };

  const wipingService = configurations?.find(configuration => configuration?.configClassId === CONFIG_CLASS_ID.WIPING);
  const configurationsOtherThanExternalOnes = configurations?.filter(config => !config?.isExternalService);
  const hasConfigurationsToShow = !!configurationsOtherThanExternalOnes?.length;

  const configClassIds = useMemo(() => {
    if (isDEPLaptopsCategory) return [CONFIG_CLASS_ID.DEP];

    return [
      CONFIG_CLASS_ID.MDM_ENROLLMENT,
      CONFIG_CLASS_ID.CREAR_ADMIN,
      CONFIG_CLASS_ID.ENCRIPTAR_BITLOCKER,
      CONFIG_CLASS_ID.APPLE_CONFIGURATOR,
      CONFIG_CLASS_ID.HASH_ID
    ];
  }, [isDEPLaptopsCategory]);

  return (
    <div className="toolConfigurationsSection">
      <div className="sectionHeader">
        <div className="sectionText sectionTitle">{title}</div>
      </div>
      {!hasConfigurations && orderId && (
        <button className="addSettingsButton" onClick={() => setShowSettingsModal(true)}>
          <span>Agregar configuraciones</span>
          <BordOneToneIcon variant="tool" standardSize={14} stroke="#383838" />
        </button>
      )}
      {hasConfigurationsToShow &&
        configurationsOtherThanExternalOnes?.map(configuration => {
          return (
            <ConfigurationStatusSection
              key={`configuration-${configuration.id}-${configuration.configClassName}`}
              configurationData={configuration}
              updateDataCallback={updateDataCallback}
              countryId={countryId}
              countryCode={countryCode}
              orderId={orderId}
              logisticServiceId={logisticServiceId}
            />
          );
        })}
      {!hasConfigurationsToShow && <div className="noConfigurationsText">No se solicitaron configuraciones</div>}
      {showExternalWipingSection && (
        <ExternalWipingSection
          updateDataCallback={updateDataCallback}
          wipingService={wipingService}
          productId={productId}
        />
      )}
      {hasConfigurationsToShow && (
        <NudosStaticAlertInComponent
          customClassname="configurationUserNotificationAlert configuration"
          type="information"
          alertOptions={alertOptions}
        />
      )}
      {showSettingsModal && orderId && organizationId && productId && (
        <AddSettingsModal
          orderId={orderId}
          productId={productId}
          organizationId={organizationId}
          configClassIds={configClassIds}
          savedOrganizationConfigs={savedOrganizationConfigs || []}
          productName={productName}
          handleAddSettingsSubmit={updateDataCallback}
          closeModal={() => setShowSettingsModal(false)}
        />
      )}
    </div>
  );
};

export default ToolConfigurationsSection;
